/**
 * The coarse time-of-day contexts the app can honestly observe.
 *
 * This is deliberately dumb: no latency heuristics, inference, or clever
 * matching. Architecture §2.9 forbids inventing signals the app cannot
 * honestly observe.
 */
export const TIME_BUCKETS = ['earlyMorning', 'morning', 'afternoon', 'evening', 'night'] as const;
export type TimeBucket = (typeof TIME_BUCKETS)[number];

/** A plan's context: a time bucket, or 'any' for a plan not tied to a time of day. */
export type PlanContext = TimeBucket | 'any';

export const EARLY_MORNING_START_HOUR = 5;
export const MORNING_START_HOUR = 8;
export const AFTERNOON_START_HOUR = 12;
export const EVENING_START_HOUR = 17;
export const NIGHT_START_HOUR = 22;

export function bucketFor(date: Date): TimeBucket {
  return bucketForHour(date.getHours());
}

/**
 * Uses the timestamp's written local hour, rather than reinterpreting its
 * instant in the timezone of the device replaying the log.
 */
export function bucketForIso(iso: string): TimeBucket {
  return bucketForHour(Number.parseInt(iso.slice(11, 13), 10));
}

/**
 * Coerce a stored context into one this build understands.
 *
 * The event payload types `context` as free text, so the log can hold a value
 * this build has never heard of — a plan written by a newer client, or one a
 * future release lets the user name themselves. Treating it as `'any'` keeps
 * that plan *offerable*: the alternative is a plan that matches no bucket and
 * is excluded from the `'any'` fallback too, and so silently never appears
 * again. A plan the user wrote should never become invisible because a version
 * number moved.
 */
export function normalizeContext(raw: string): PlanContext {
  return (TIME_BUCKETS as readonly string[]).includes(raw) ? (raw as TimeBucket) : 'any';
}

export function contextMatches(context: PlanContext, at: Date | string): boolean {
  return context === 'any' || context === (typeof at === 'string' ? bucketForIso(at) : bucketFor(at));
}

function bucketForHour(hour: number): TimeBucket {
  if (hour >= EARLY_MORNING_START_HOUR && hour < MORNING_START_HOUR) return 'earlyMorning';
  if (hour >= MORNING_START_HOUR && hour < AFTERNOON_START_HOUR) return 'morning';
  if (hour >= AFTERNOON_START_HOUR && hour < EVENING_START_HOUR) return 'afternoon';
  if (hour >= EVENING_START_HOUR && hour < NIGHT_START_HOUR) return 'evening';
  return 'night';
}
